// Getting an image into a text template.
//
// Images are embedded in the template text as data (a base64 data URI), so nothing
// has to be stored or fetched separately: the candidate's page and the signed PDF
// both show them from the text alone. The text lives in one database row, so every
// image is scaled down and re-encoded before it goes in.

export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

// Wide enough to stay sharp on a printed A4 page, small enough to keep the text light.
export const MAX_IMAGE_WIDTH = 1000;
// Rough ceiling for one embedded image (the data URI's length in characters).
export const MAX_IMAGE_DATA_CHARS = 350_000;

export function isAcceptedImage(file) {
  return !!file && ACCEPTED_IMAGE_TYPES.includes(file.type);
}

// How wide an image is drawn in the text, as a share of the page width.
export const MIN_IMAGE_PERCENT = 10;
export const MAX_IMAGE_PERCENT = 100;
export const IMAGE_PERCENT_STEP = 5;

// The percentage held in an image's width attribute ("50%"), kept within the slider's
// range, or null when the image has no share set (it is shown at its natural size).
export function widthPercent(width) {
  const match = /^(\d{1,4})%$/.exec(String(width ?? '').trim());
  if (!match) return null;
  return Math.min(MAX_IMAGE_PERCENT, Math.max(MIN_IMAGE_PERCENT, Number(match[1])));
}

// Scale to fit `maxWidth` without ever enlarging, keeping the aspect ratio.
export function scaledSize(width, height, maxWidth = MAX_IMAGE_WIDTH) {
  if (!width || !height) return { width: 0, height: 0 };
  if (width <= maxWidth) return { width, height };
  const ratio = maxWidth / width;
  return { width: maxWidth, height: Math.max(1, Math.round(height * ratio)) };
}

// A readable name for the image's alt text: the file name without its extension.
export function altTextFromName(name) {
  return String(name || 'Image').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim().slice(0, 100) || 'Image';
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as an image.'));
    };
    img.src = url;
  });
}

// Encode an image at the given size. PNG keeps transparency (logos); if that is still
// too big we fall back to JPEG on a white background at decreasing quality.
function encode(img, { width, height }) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const attempts = [
    () => { ctx.clearRect(0, 0, width, height); ctx.drawImage(img, 0, 0, width, height); return canvas.toDataURL('image/png'); },
    ...[0.85, 0.7, 0.5].map((quality) => () => {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      return canvas.toDataURL('image/jpeg', quality);
    }),
  ];
  for (const attempt of attempts) {
    const dataUrl = attempt();
    if (dataUrl.length <= MAX_IMAGE_DATA_CHARS) return dataUrl;
  }
  return null;
}

/**
 * Turn a chosen/pasted/dropped file into something that can go in the text:
 * `{ src, alt, width, height }` with `src` a data URI. Throws an Error with a message
 * fit to show the user.
 */
export async function embedImageFile(file) {
  if (!isAcceptedImage(file)) {
    throw new Error('Use a PNG, JPEG, GIF or WebP image.');
  }
  const img = await loadImage(file);
  const size = scaledSize(img.naturalWidth, img.naturalHeight);
  let dataUrl = encode(img, size);
  if (!dataUrl) {
    // Still too heavy at full width: halve it once before giving up.
    dataUrl = encode(img, scaledSize(size.width, size.height, Math.round(size.width / 2)));
  }
  if (!dataUrl) {
    throw new Error('That image is too detailed to embed. Try a smaller or simpler image.');
  }
  return { src: dataUrl, alt: altTextFromName(file.name), width: size.width, height: size.height };
}
